import { useEffect, useState } from "react";

import { GlobalStyles } from "@mui/material";
import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material/styles";
import { AppCacheProvider } from "@mui/material-nextjs/v15-pagesRouter";
import { LicenseInfo } from "@mui/x-license-pro";

import { ErrorHandler } from "../components/ErrorHandler/ErrorHandler";
import { trpc } from "../helpers/trpc";
import { ConsentModeProvider } from "../providers/ConsentModeProvider";
import { DatadogProvider } from "../providers/DatadogProvider";
import { SessionContextProvider } from "../providers/SessionProvider";

import "../styles.css";
import "react-image-crop/dist/ReactCrop.css";
import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";
import { QueryStateContextProvider } from "@/hooks/useQueryStateContext";
import { zeffyTheme, superAdminTheme } from "@/theme/theme";
import { MyAppProps } from "@/types/page";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_X_LICENSE_KEY as string);

export enum WebVitals {
  "FCP" = "FCP",
  "FID" = "FID",
}

export function reportWebVitals(metric: { id: string; name: string; label: string; value: number; startTime: number }) {
  const { id, name, label, value } = metric;

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("Web vitals:", name, value);
    return;
  }

  if (!window.gtag) {
    // eslint-disable-next-line no-console
    console.error("window.gtag is not defined, verify that gtag script is imported via <Script> or via gTagManager");
    return;
  }

  window.gtag("event", name, {
    event_category: label === "web-vital" ? "Web Vitals" : "Next.js custom metric",
    value: Math.round(name === "CLS" ? value * 1000 : value), // values must be integers
    event_label: id, // id unique to current page load
    non_interaction: true, // avoids affecting bounce rate.
  });
}

const MyApp = (props: MyAppProps) => {
  const { Component, pageProps, emotionCache, ...otherProps } = props;
  const [isAdminTheme, setIsAdminTheme] = useState(false);

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles?.parentElement?.removeChild(jssStyles);
    }
  }, []);

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page: React.ReactElement<unknown>) => page);

  return (
    <AppCacheProvider emotionCache={emotionCache} {...otherProps}>
      <QueryStateContextProvider>
        <ErrorHandler>
          <SessionContextProvider setIsAdminTheme={setIsAdminTheme}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={isAdminTheme ? superAdminTheme : zeffyTheme}>
                <GlobalStyles
                  styles={{
                    "@keyframes mui-auto-fill": { from: { display: "block" } },
                    "@keyframes mui-auto-fill-cancel": { from: { display: "block" } },
                  }}
                />
                <ZeffySnackbarProvider>
                  <ConsentModeProvider>
                    <DatadogProvider>
                      <>{getLayout(<Component {...pageProps} />)}</>
                    </DatadogProvider>
                  </ConsentModeProvider>
                </ZeffySnackbarProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </SessionContextProvider>
        </ErrorHandler>
      </QueryStateContextProvider>
    </AppCacheProvider>
  );
};

export default trpc.withTRPC(MyApp);
